<template>
  <ReportModal :item-id="currentId" origin="exhibitionStore"></ReportModal>
  <div class="my-10 flex flex-col gap-5">
    <div class="">
      <div v-if="bannersLoaded">
        <Splide
          @splide:move="stopVideo"
          ref="splide"
          class="w-full"
          :options="{
            slideFocus: true,
            type: 'loop',
            autoplay: false,
            pagination: false,
            pauseOnHover: false,
          }"
          :has-track="false"
          aria-label="similars"
        >
          <SplideTrack class="py-4">
            <SplideSlide v-for="el in detail?.medias" :key="keyvideo[`video${el.id}`]">
              <video
                v-if="isVideo(el.path)"
                :ref="'video' + el.id"
                controls
                controlsList="nodownload"
                :src="el?.path"
                class="object-contain h-[450px] w-full"
              ></video>
              <img
                v-else
                class="rounded-t h-[450px] w-full object-fill"
                :src="el?.path"
                alt="store-banner"
              />
            </SplideSlide>
          </SplideTrack>
        </Splide>
      </div>
      <div
        class="rounded-lg bg-white mb-4 flex items-center gap-6 p-4 min-w-0"
        :class="{ 'bg-gradient-to-br from-[#ECECFE] to-[#D3D5FD]': detail?.is_vip }"
      >
        <div class="flex-1 min-w-0">
          <h1 class="font-bold text-2xl text-main-color truncate block">
            {{ getTranslatedStoreField('name') || detail?.caption }}
          </h1>
        </div>

        <button
          v-if="detail?.subscription"
          class="shrink-0 w-max rounded-lg px-8 font-bold"
          :class="getSubcriptionType?.color"
        >
          {{ getSubcriptionType?.name }}
        </button>

        <div
          v-if="detail?.is_vip"
          class="shrink-0 px-8 bg-[#7951F9] rounded-lg text-white font-bold text-2xl text-end"
        >
          VIP
        </div>

        <VueStarRating
          class="shrink-0"
          :rounded-corners="true"
          :star-size="15"
          active-color="#ffb300"
          inactive-color="#f2f2f2"
          :border-width="3"
          border-color="#ffb300"
          :show-rating="false"
          :read-only="true"
          :increment="0.5"
          :rating="detail?.rating"
        ></VueStarRating>

        <div class="flex items-center gap-1 shrink-0 whitespace-nowrap" v-if="detail?.work_hours">
          <IconClock class="shrink-0" />
          <span>
            {{ detail?.work_hours[getDay.toLowerCase()] }}
          </span>
        </div>

        <div class="ml-auto flex gap-5 shrink-0">
          <button
            class="hover:bg-gray-100 active:bg-gray-200 px-2 rounded-full"
            @click="copy(source)"
          >
            <IconShare v-if="!copied" />
            <span v-else>Link copied!</span>
          </button>
          <ReportButton class="w-max !py-1.5 px-5"></ReportButton>
        </div>
      </div>
      <div class="w-full flex items-center justify-start gap-5 bg-white p-3 rounded-b">
        <button
          @click="tabIndex = 0"
          class="text-[#828282] text-base font-medium ml-2 cursor-pointer"
          :class="{
            'text-main-color border border-x-0 border-t-0 border-[#409DBA]': tabIndex === 0,
          }"
        >
          {{ $t('page.allProducts') }}
        </button>
        <button
          @click="tabIndex = 1"
          class="text-[#828282] text-base font-medium ml-2 cursor-pointer"
          :class="{
            'text-main-color border border-x-0 border-t-0 border-[#409DBA]': tabIndex === 1,
          }"
        >
          {{ $t('page.aboutRestaurant') }}
        </button>
      </div>
    </div>
    <div v-if="tabIndex === 0">
      <div
        v-if="products?.length > 0"
        class="mb-4 flex flex-wrap gap-3 px-1 bg-white sticky top-0 z-40 rounded"
      >
        <Splide
          class="w-full"
          :options="SPLIDE_PILL_OPTIONS"
          :has-track="false"
          aria-label="similars"
        >
          <SplideTrack class="py-4">
            <SplideSlide class="cursor-pointer" v-for="(el, idx) in products" :key="idx">
              <router-link
                :to="{
                  name: 'foodRestaurantDetail',
                  params: { id: currentId },
                  query: { category: el.id },
                }"
                class="w-max px-4 cursor-pointer items-center gap-1 justify-center p-3 text-lg bg-white rounded-md border border-solid border-gp-grey-70"
                :class="{ '!bg-main-color text-white is-active': currentCategory == el.id }"
              >
                {{ el.name }}</router-link
              >
            </SplideSlide>
          </SplideTrack>
        </Splide>
      </div>
      <div v-for="(el, idx) in products" :key="idx">
        <p :id="el.id" class="font-bold text-xl mt-10 m-4">
          {{ el.name }}
        </p>
        <div class="grid grid-cols-4 gap-2 max-lg:grid-cols-3 max-md:grid-cols-2">
          <router-link
            v-for="item in el.products"
            :key="item.id"
            :to="{ name: 'foodRestaurantProductDetail', params: { id: item.id } }"
          >
            <FoodCard @likeAction="likeAction" :detail="item"></FoodCard>
          </router-link>
        </div>
      </div>
    </div>
    <div v-else-if="tabIndex === 1" class="w-full grid grid-cols-2 gap-16 bg-white p-8 rounded-b">
      <div class="min-w-0">
        <div class="flex justify-between items-center text-main-color mb-3 min-w-0">
          <p class="text-3xl font-bold truncate min-w-0 flex-1 pr-4">
            {{ detail?.name }}
          </p>
          <div class="flex items-center gap-2 shrink-0">
            <IconEye></IconEye>
            <span class="font-bold text-sm">{{ detail?.view_count }}</span>
          </div>
        </div>
        <div class="text-[#333333] text-base pb-4 border-b border-[#E3E3E3]">
          <p class="font-light">{{ detail?.business_type }}</p>
        </div>
        <div class="mt-3">
          <p class="tetx-lg text-main-color mb-3">
            {{ $t('page.status') }}:
            <span class="font-semibold"
              >{{ detail?.subscription }} {{ detail?.is_vip ? ', VIP' : '' }}</span
            >
          </p>
          <p class="tetx-lg text-main-color mb-3">
            {{ $t('page.productCount') }}:
            <span class="font-semibold">{{ detail?.objects__count }}</span>
          </p>
          <p class="tetx-lg text-main-color mb-3">
            {{ $t('page.location') }}: <span class="font-semibold">{{ detail?.location }}</span>
          </p>

          <p v-if="detail?.phone" class="tetx-lg text-main-color mb-3">
            {{ $t('page.phoneNumbers') }}:
            <a :href="`tel:${detail.phone}`" class="font-semibold">{{ detail.phone }}</a>
          </p>
        </div>
      </div>
      <div class="min-w-0">
        <div class="accordion">
          <Accordion :label="$t('page.info')">
            <p>{{ getTranslatedStoreField('description') }}</p>
          </Accordion>
          <Accordion v-if="detail?.type == 0" :label="$t('page.phoneNumbers')">
            <div class="flex flex-col gap-2">
              <a
                v-for="(phone, index) in detail?.phone_numbers"
                :key="index"
                :href="`tel:${phone}`"
                >{{ phone }}</a
              >
            </div>
          </Accordion>

          <Accordion v-if="detail?.type == 0" :label="$t('nav.contact')">
            <div class="flex flex-col gap-2">
              <a v-for="(ct, index) in detail?.contacts" :key="index" :href="ct.value"
                >{{ CONTACT_TYPE_OPTIONS[ct.type] }}: {{ ct.value }}</a
              >
            </div>
          </Accordion>
          <Accordion :label="$t('page.workingHours')">
            <div class="flex flex-col gap-2">
              <p>
                <span class="font-semibold">{{ $t('page.monday') }}: </span
                >{{ detail?.work_hours?.monday }}
              </p>
              <p>
                <span class="font-semibold">{{ $t('page.tuesday') }}: </span
                >{{ detail?.work_hours?.tuesday }}
              </p>
              <p>
                <span class="font-semibold">{{ $t('page.wednesday') }}: </span
                >{{ detail?.work_hours?.wednesday }}
              </p>
              <p>
                <span class="font-semibold">{{ $t('page.thursday') }}: </span
                >{{ detail?.work_hours?.thursday }}
              </p>
              <p>
                <span class="font-semibold">{{ $t('page.friday') }}: </span
                >{{ detail?.work_hours?.friday }}
              </p>
              <p>
                <span class="font-semibold">{{ $t('page.saturday') }}: </span
                >{{ detail?.work_hours?.saturday }}
              </p>
              <p>
                <span class="font-semibold">{{ $t('page.sunday') }}: </span
                >{{ detail?.work_hours?.sunday }}
              </p>
            </div>
          </Accordion>
          <Accordion :label="$t('page.deliveryAbout')">
            <p>{{ getTranslatedStoreField('delivery_policy') }}</p>
          </Accordion>
          <Accordion :label="$t('page.deliveryTerms')">
            <DeliveryTerms :detail="detail" />
          </Accordion>
          <Accordion :label="$t('page.paymentRefund')">
            <p>{{ getTranslatedStoreField('payment_policy') }}</p>
          </Accordion>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import Accordion from '@cmp/Accordion.vue';
import DeliveryTerms from '@cmp/DeliveryTerms.vue';
import SortButton from '@cmp/SortButton.vue';
import FoodCard from '@cmp/food/FoodCard.vue';
import IconEye from '@icons/IconEye.vue';
import IconShare from '@icons/IconShare.vue';
import IconSearch from '@icons/IconSearch.vue';
import IconClock from '@icons/IconClock.vue';
import ReportButton from '@cmp/modals/buttons/ReportButton.vue';
import ReportModal from '@cmp/modals/ReportModal.vue';
import { ref, nextTick } from 'vue';
import { useClipboard } from '@vueuse/core';
import { SORT_OPTIONS, SPLIDE_PILL_OPTIONS, CONTACT_TYPE_OPTIONS } from '@/constants';
import VueStarRating from 'vue-star-rating';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide';
import { isVideo, updateLastSeen } from '@/helpers';
export default {
  name: 'foodRestaurantDetail',
  components: {
    Accordion,
    DeliveryTerms,
    SortButton,
    FoodCard,
    IconEye,
    IconShare,
    IconSearch,
    Splide,
    SplideSlide,
    SplideTrack,
    VueStarRating,
    ReportButton,
    ReportModal,
    IconClock,
  },
  watch: {
    '$route.query.category': {
      //   immediate: true,
      async handler(newValue, oldValue) {
        if (newValue != oldValue) {
          this.currentCategory = newValue;
          this.products = [];
          await this.getStoreProducts();
          this.scrollToPlace();
        }
      },
    },
    '$route.query.ordering': {
      //   immediate: true,
      async handler(newValue, oldValue) {
        if (newValue != oldValue) {
          this.currentSort = newValue;
          this.products = [];
          await this.getStoreProducts();
        }
      },
    },
    '$route.query.search': {
      //   immediate: true,
      async handler(newValue, oldValue) {
        if (newValue != oldValue) {
          this.currentSearch = newValue;
          this.products = [];
          await this.getStoreProducts();
        }
      },
    },
  },
  setup() {
    const source = ref(window.location.href);
    const { text, copy, copied, isSupported } = useClipboard({ source });
    return {
      text,
      copy,
      copied,
      isSupported,
    };
  },
  data() {
    return {
      loading: false,
      bannersLoaded: false,
      detail: {},
      products: [],
      tabIndex: 0,
      keyvideo: {},
      SORT_OPTIONS: SORT_OPTIONS,
      currentSort: '',
      currentRelevancy: null,
      currentCategory: null,
      currentSearch: null,
      SPLIDE_PILL_OPTIONS: SPLIDE_PILL_OPTIONS,
      CONTACT_TYPE_OPTIONS: CONTACT_TYPE_OPTIONS,
    };
  },
  methods: {
    isVideo,
    getCurrentLang() {
      return this.$i18n?.locale || localStorage.getItem('lang') || 'tk';
    },

    getTranslatedStoreField(field) {
      const lang = this.getCurrentLang();

      return (
        this.detail?.[`${field}_${lang}`] ||
        this.detail?.[field] ||
        this.detail?.[`${field}_tk`] ||
        this.detail?.[`${field}_ru`] ||
        ''
      );
    },

    scrollToPlace() {
      nextTick(() => {
        const element = document.getElementById(this.currentCategory);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      });
    },

    scrollToPlace() {
      nextTick(() => {
        const element = document.getElementById(this.currentCategory);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      });
    },
    async getStoreDetail() {
      this.loading = true;
      try {
        const response = await api.get(`exhibition/stores/${this.currentId}/`);
        this.detail = response.data;
        this.detail?.medias.forEach(element => {
          if (this.isVideo(element.path)) {
            let v = 'video' + element.id;
            this.keyvideo[v] = 1;
          }
          this.bannersLoaded = true;
        });
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },
    searchProducts() {
      if (this.currentSearch === '') {
        this.currentSearch = null;
      }
      this.$router.push({
        name: 'foodRestaurantDetail',
        params: { id: this.currentId },
        query: {
          category: this.currentCategory,
          store: this.currentId,
          search: this.currentSearch,
          ordering: this.currentSort,
        },
      });
    },
    async getStoreProducts() {
      this.loading = true;
      try {
        const response = await api.get(`exhibition/stores/${this.currentId}/category_products/`);
        this.products = response.data;
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },
    async likeAction(id, like) {
      if (this.$store.authStatus === false) {
        this.$store.setState('loginModal', true);
        return;
      }
      try {
        const response = await api.post(`exhibition/products/${id}/like/`, { is_liked: like });
        this.$store.getLiked('exhibition');
      } catch (e) {
        console.log(e);
      }
    },

    handleRelevancy(e) {
      this.currentSort = e;
      this.searchProducts();
    },
    stopVideo() {
      this.detail?.medias.forEach(element => {
        if (this.isVideo(element.path)) {
          let v = 'video' + element.id;
          this.keyvideo[v] += 1;
        }
      });
    },
  },
  computed: {
    getSubcriptionType() {
      switch (this.detail.subscription) {
        case 'Gold':
          return { color: 'bg-[#FABB18] text-black', name: 'Gold' };
        case 'Silver':
          return { color: 'bg-[#C0C0C0] text-black', name: 'Silver' };
        case 'Bronze':
          return { color: 'bg-[#AE7145] text-white', name: 'Bronze' };
        default:
          break;
      }
    },
    getDay() {
      if (this.detail?.work_hours) {
        let date = new Date();
        return date.toLocaleString('en-us', { weekday: 'long' });
      }
      return null;
    },
  },
  async created() {
    this.currentId = this.$route.params.id;
    this.currentCategory = this.$route.query.category;
    this.currentSearch = this.$route.query.search;
    this.currentSort = this.$route.query.sort_by;
    this.currentRelevancy = this.SORT_OPTIONS.filter(el => el.value == this.currentSort)[0];
    await this.getStoreProducts();
    await this.getStoreDetail();
    this.scrollToPlace();
    updateLastSeen('exhibition_products');
  },
};
</script>
